import React, { useState, useEffect } from 'react';
import usePedidosKDS from '../Tienda/usePedidosKDS';
import { imprimirComandaCocina } from '../../services/Impresion';
import { getRepartidoresActivos } from '../../services/repartidoresServicio';
import { anularPedidoApi } from '../../services/pedidosServicio';
import config, { resolveImagenUrl } from '../../config/apiConfig';
import { toast, ToastContainer } from 'react-toastify';

// 🚩 Asignación de repartidor OCULTA por ahora (el KDS todavía no se habilita
// a clientes). El endpoint que usa (/api/pedidos/asignar_repartidor) tiene un
// hueco de seguridad pendiente de arreglar antes de reactivar esto — ver
// auditoría de hoy. Para reactivar: cambiar a true Y arreglar el backend primero.
const REPARTIDOR_KDS_HABILITADO = false;

var STATUS_TO_ID = { 'Recibida': 1, 'Preparando': 2, 'Lista': 3, 'EnReparto': 4, 'Entregada': 5 };

var getTimeElapsed = function (timestamp) {
    if (!timestamp) return "0s";
    var mins = Math.floor((Date.now() - new Date(timestamp)) / 60000);
    return mins < 1 ? "Recién" : mins + "m";
};

const getVariantesTexto = (item) => {
    if (!item) return "";
    if (item.variantes_detalles) return item.variantes_detalles;
    if (item.variantes) return item.variantes;
    if (Array.isArray(item.mods)) return item.mods.map(m => (typeof m === 'object' ? (m.nombre || m.name) : m)).join(', ');
    if (Array.isArray(item.opciones_escogidas)) return item.opciones_escogidas.map(o => o.nombre || o.nombre_opcion).join(', ');
    return "";
};

const getMinutesElapsed = (timestamp) => {
    if (!timestamp) return 0;
    return Math.floor((Date.now() - new Date(timestamp)) / 60000);
};

const RenderVariantesDetalle = ({ texto, fontSize = "1rem" }) => {
    if (!texto) return null;
    const grupos = String(texto).split(' | ');

    return (
        <div className="mt-1 text-left">
            {grupos.map((grupoRaw, idx) => {
                const parts = grupoRaw.split(']: ');
                const tituloGrupo = parts.length > 1 ? parts[0].replace('[', '') : '';
                const contenido = parts.length > 1 ? parts[1] : parts[0];

                return (
                    <div key={idx} className="mb-2 p-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', borderLeft: '4px solid #666' }}>
                        {tituloGrupo && (
                            <div className="small font-weight-bold text-muted text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.75rem' }}>
                                {tituloGrupo}
                            </div>
                        )}
                        <div className="d-flex flex-wrap align-items-center">
                            {(() => {
                                let currentText = contenido;
                                if (currentText.includes('SIN:') || currentText.includes('EXTRA:')) {
                                    return currentText.split(' ').map((word, wIdx) => {
                                        if (word === 'SIN:') return <span key={wIdx} className="badge badge-danger mr-1" style={{ fontSize: fontSize }}>SIN</span>;
                                        if (word === 'EXTRA:') return <span key={wIdx} className="badge badge-success mr-1" style={{ fontSize: fontSize }}>EXTRA</span>;
                                        return <span key={wIdx} className="font-weight-bold mr-1" style={{ fontSize: fontSize }}>{word.replace(',', '')}</span>;
                                    });
                                } else {
                                    return <span className="font-weight-bold" style={{ fontSize: fontSize }}>{currentText}</span>;
                                }
                            })()}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// ==============================================================================
// 📸 MODAL DE VISUALIZACIÓN DE COMPROBANTE
// ==============================================================================
const ComprobanteModal = ({ url, onClose }) => {
    if (!url) return null;

    const urlCompleta = resolveImagenUrl(url);

    return (
        <div className="modal-custom" onClick={onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
            zIndex: 200010, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }}>
            <div className="bg-white p-4 rounded shadow-lg" style={{ maxWidth: '600px', width: '100%', borderRadius: '20px' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="font-weight-bold m-0">📎 Comprobante de pago</h4>
                    <button className="btn btn-sm btn-danger rounded-circle" onClick={onClose}
                        style={{ width: '36px', height: '36px' }}>✕</button>
                </div>
                <img src={urlCompleta} alt="Comprobante"
                    style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '12px', border: '1px solid #ddd' }} />
                <div className="text-center mt-3">
                    <a href={urlCompleta} target="_blank" rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm">
                        <i className="fas fa-external-link-alt mr-1"></i> Abrir en nueva pestaña
                    </a>
                </div>
            </div>
        </div>
    );
};

// ==============================================================================
// 🖼️ BADGE DE MÉTODO DE PAGO
// ==============================================================================
const MetodoPagoBadge = ({ metodoPago, urlComprobante, onVerComprobante }) => {
    const mp = (metodoPago || "").toUpperCase();

    // Mapa de métodos de pago a iconos y colores
    const configMetodo = {
        'TRANSFERENCIA': { icon: 'fa-university', color: '#6f42c1', label: 'Transferencia' },
        'EFECTIVO': { icon: 'fa-money-bill-wave', color: '#28a745', label: 'Efectivo' },
        'TARJETA': { icon: 'fa-credit-card', color: '#007bff', label: 'Tarjeta' },
        'WEBPAY': { icon: 'fa-credit-card', color: '#17a2b8', label: 'Webpay' },
        'PENDIENTE': { icon: 'fa-hourglass-half', color: '#dc3545', label: 'Pendiente' },
        'PLATAFORMA': { icon: 'fa-mobile-alt', color: '#6f42c1', label: 'App' },
    };

    // Buscar coincidencia parcial
    let encontrado = null;
    for (const [key, val] of Object.entries(configMetodo)) {
        if (mp.includes(key)) { encontrado = val; break; }
    }
    if (!encontrado && mp) {
        encontrado = { icon: 'fa-credit-card', color: '#6c757d', label: metodoPago };
    }
    if (!encontrado) return null;

    const tieneComprobante = urlComprobante && urlComprobante.length > 0;

    return (
        <div className="d-inline-flex align-items-center">
            <span className="badge mr-1 px-2 py-1 text-white font-weight-bold d-inline-flex align-items-center"
                style={{ backgroundColor: encontrado.color, fontSize: '0.7rem', gap: '4px' }}>
                <i className={`fas ${encontrado.icon}`}></i>
                {encontrado.label}
            </span>
            {tieneComprobante && (
                <span className="badge badge-warning px-2 py-1 font-weight-bold d-inline-flex align-items-center"
                    style={{ fontSize: '0.7rem', gap: '4px', cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); onVerComprobante && onVerComprobante(urlComprobante); }}
                    title="Ver comprobante de pago">
                    📎
                </span>
            )}
        </div>
    );
};

// ==============================================================================
// 🔍 MODAL ZOOM (MEJORADO CON COMPROBANTE)
// ==============================================================================
const ZoomOrderModal = function (props) {
    var order = props.order;
    if (!order) return null;

    return (
        <div className="modal-custom" onClick={props.onClose} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.95)',
            zIndex: 100010, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px'
        }}>
            <div className="bg-white w-100 shadow-2xl d-flex flex-column text-dark text-left"
                style={{ maxWidth: '1000px', maxHeight: '90vh', borderRadius: '30px', overflow: 'hidden' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="p-4 border-bottom bg-light d-flex justify-content-between align-items-center">
                    <div>
                        <h1 className="display-1 font-weight-bold m-0 text-primary" style={{ lineHeight: '0.8' }}>#{order.display_id}</h1>
                        <div className="mt-2 d-flex align-items-center" style={{ gap: '8px' }}>
                            <span className="badge badge-info px-3 py-2 text-uppercase" style={{ fontSize: '1.2rem' }}>{order.type}</span>
                            <MetodoPagoBadge
                                metodoPago={order.metodo_pago}
                                urlComprobante={order.url_comprobante}
                                onVerComprobante={props.onVerComprobante}
                            />
                        </div>
                    </div>
                    <div className="text-right">
                        <h2 className="font-weight-bold m-0" style={{ fontSize: '2.5rem' }}>{order.customer ? (order.customer.name || order.customer.nombre) : 'CLIENTE'}</h2>
                        <div className="h4 m-0 text-muted font-weight-bold"><i className="fas fa-clock mr-2"></i>{getTimeElapsed(order.time)}</div>
                    </div>
                </div>
                <div id="zoom-scroll-container" className="p-4 overflow-auto custom-scrollbar" style={{ flex: 1, backgroundColor: '#fff' }}>
                    {order.observacion && (
                        <div className="alert alert-warning border-dark mb-4 p-3 shadow-sm text-center">
                            <h2 className="m-0 font-weight-bold text-danger" style={{ fontSize: '2.2rem' }}><i className="fas fa-exclamation-triangle mr-3"></i>{order.observacion.toUpperCase()}</h2>
                        </div>
                    )}
                    <div className="list-group list-group-flush">
                        {order.items.map(function (item, i) {
                            var vText = getVariantesTexto(item);
                            return (
                                <div key={i} className="mb-3 p-3 bg-light rounded-xl border-left shadow-sm" style={{ borderLeft: '20px solid #007bff' }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <div className="bg-dark text-white rounded px-3 py-1 mr-3" style={{ fontSize: '3rem', fontWeight: '900', minWidth: '80px', textAlign: 'center' }}>{item.cantidad}</div>
                                        <div className="flex-grow-1">
                                            <h1 className="m-0 font-weight-black text-uppercase" style={{ fontSize: '2.5rem', lineHeight: '1' }}>{item.nombre}</h1>
                                        </div>
                                    </div>
                                    <div className="ml-2 pl-2 border-left">
                                        <RenderVariantesDetalle texto={vText} fontSize="1.5rem" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="p-3 bg-dark text-center shadow-lg">
                    <button className="btn btn-success btn-block btn-lg py-4 font-weight-bold text-uppercase" style={{ fontSize: '1.8rem', borderRadius: '50px' }} onClick={props.onClose}>
                        VOLVER (ENTER) <span className="ml-4 opacity-50" style={{ fontSize: '1rem' }}>[ / ] Sube | [ - ] Baja</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================================================
// 📦 COMPONENTE: TARJETA DE PEDIDO (MEJORADA CON COMPROBANTE)
// ==============================================================================
const OrderCard = ({ order, onStatusChange, onReimprimir, onZoom, onVerComprobante, tiempoEnLista }) => {
    // 1. VARIABLES BASICAS
    const estaPagado = order.pagado === 1 || order.pagado === true;
    const type = (order.type || "").toUpperCase();
    const metodoPago = (order.metodo_pago || "").toUpperCase();
    const minutos = getMinutesElapsed(order.time);

    // 2. DETECCIÓN DE APP
    const esApp = type.includes('APP') || metodoPago === 'PLATAFORMA';

    let nombreApp = "APP";
    let idExterno = null;
    let observacionMostrar = order.observacion || "";

    if (esApp && observacionMostrar) {
        const match = observacionMostrar.match(/^\[(.*?):(.*?)\]/);
        if (match) {
            nombreApp = match[1].trim().toUpperCase();
            idExterno = match[2].trim();
            observacionMostrar = observacionMostrar.replace(match[0], '').trim();
        }
    }

    // 3. ESTILOS DE DEMORA
    let delayStyle = { border: 'none', badge: null, animation: '' };

    if (order.status === 'Lista' && estaPagado && tiempoEnLista !== null) {
        if (tiempoEnLista >= 100) {
            delayStyle.animation = 'anim-blink-fast';
            delayStyle.border = '4px solid #28a745';
        } else if (tiempoEnLista >= 60) {
            delayStyle.animation = 'anim-blink-slow';
        }
    } else if (order.status !== 'Lista') {
        if (minutos >= 15 && minutos < 25) {
            delayStyle = { border: '4px solid #fd7e14', badge: <span className="badge badge-warning ml-2 anim-pulse">DEMORADO</span>, animation: 'shadow-orange' };
        } else if (minutos >= 25) {
            delayStyle = { border: '5px solid #dc3545', badge: <span className="badge badge-danger ml-2 anim-flash-red">PRIORIDAD MÁXIMA 🔥</span>, animation: 'shadow-red-burning' };
        }
    }

    // 4. ICONOS Y COLORES
    const getOrderIcon = () => {
        if (esApp) return { icon: 'fa-mobile-alt', color: '#6f42c1', bgHeader: '#5e35b1' };
        if (type.includes('DELIVERY')) return { icon: 'fa-motorcycle', color: '#00d1b2', bgHeader: '#00d1b2' };
        if (type.includes('RETIRO')) return { icon: 'fa-shopping-bag', color: '#ffdd57', bgHeader: '#ffdd57' };
        return { icon: 'fa-utensils', color: '#3273dc', bgHeader: '#3273dc' };
    };

    const orderStyle = getOrderIcon();
    const headerBg = (minutos >= 25 && order.status !== 'Lista') ? '#dc3545' : orderStyle.bgHeader;
    const headerTextColor = (type.includes('RETIRO') && headerBg === '#ffdd57') ? '#000' : '#fff';

    return (
        <div className={`card mb-3 shadow-lg overflow-hidden ${delayStyle.animation}`}
            style={{ borderRadius: '20px', background: '#fff', border: delayStyle.border || '4px solid transparent' }}>

            {/* --- HEADER --- */}
            <div className="p-2 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: headerBg, color: headerTextColor }}>

                <div className="d-flex align-items-center">
                    <div className="rounded-lg px-2 py-1 mr-2 d-flex align-items-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <i className={`fas ${orderStyle.icon} mr-1`}></i>
                        <h4 className="m-0 font-weight-bold">{order.display_id}</h4>
                    </div>

                    {esApp && idExterno && (
                        <div className="bg-white text-dark rounded px-2 py-1 mr-2 shadow-sm d-flex flex-column align-items-center" style={{ lineHeight: '1' }}>
                            <small className="font-weight-bold text-muted" style={{ fontSize: '0.5rem' }}>{nombreApp}</small>
                            <span className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>{idExterno}</span>
                        </div>
                    )}

                    {delayStyle.badge}
                    {!estaPagado && !esApp && <span className="badge badge-danger ml-2 anim-pulse border border-white">PAGO PENDIENTE</span>}
                </div>

                <div className="text-right">
                    <div className={`h5 m-0 font-weight-bold`}>
                        <i className="fas fa-clock mr-1"></i>{minutos}m
                    </div>
                </div>
            </div>

            {/* --- CLIENTE + MÉTODO DE PAGO --- */}
            <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: '#fcfcfc' }}>
                <div style={{ maxWidth: '70%' }}>
                    <h5 className="font-weight-bold m-0 text-dark text-truncate">
                        {order.customer ? (order.customer.name || order.customer.nombre) : 'CLIENTE'}
                    </h5>
                    {(type.includes('DELIVERY') || esApp) && order.customer && (
                        <div className="small text-muted font-weight-bold text-truncate">
                            <i className="fas fa-map-marker-alt text-danger mr-1"></i>
                            {order.customer.address || order.customer.direccion}
                        </div>
                    )}
                </div>
                <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    {/* 🆕 BADGE DE MÉTODO DE PAGO CON COMPROBANTE */}
                    <MetodoPagoBadge
                        metodoPago={order.metodo_pago}
                        urlComprobante={order.url_comprobante}
                        onVerComprobante={onVerComprobante}
                    />
                    <button className="btn btn-sm btn-light border shadow-sm" onClick={(e) => { e.stopPropagation(); onReimprimir(order); }}><i className="fas fa-print"></i></button>
                    <button className="btn btn-sm btn-light border shadow-sm" onClick={(e) => { e.stopPropagation(); onZoom(order); }}><i className="fas fa-search-plus"></i></button>
                </div>
            </div>

            {/* --- OBSERVACION --- */}
            {observacionMostrar && (
                <div className="mx-2 mt-2 p-2 rounded-lg"
                    style={{ backgroundColor: minutos >= 25 ? '#ffdbdb' : '#fff3cd', borderLeft: '5px solid #ffc107' }}>
                    <div className="font-weight-bold text-dark" style={{ fontSize: '0.9rem' }}>
                        <i className="fas fa-sticky-note mr-2 text-warning"></i>{observacionMostrar.toUpperCase()}
                    </div>
                </div>
            )}

            {/* --- ITEMS --- */}
            <div className="card-body p-2 text-dark bg-white text-left">
                <ul className="list-unstyled m-0">
                    {order.items.map((it, idx) => {
                        const esNuevo = it.impreso === false || it.impreso === undefined;
                        const mostrarListoGlobal = order.status === 'Lista';

                        return (
                            <li key={idx} className="mb-2 pb-2 border-bottom"
                                style={(esNuevo && order.status !== 'Lista') ? { backgroundColor: '#fff3cd' } : {}}>

                                <div className="d-flex align-items-start">
                                    {order.status !== 'Lista' && esNuevo && (
                                        <span className="badge badge-danger mr-2 shadow-sm animate-pulse">NUEVO</span>
                                    )}

                                    {mostrarListoGlobal && (
                                        <span className="badge badge-success mr-2"><i className="fas fa-check"></i> LISTO</span>
                                    )}

                                    <div className={`badge ${esNuevo ? 'badge-dark' : 'badge-secondary'} mr-2 mt-1`} style={{ fontSize: '1rem', width: '30px' }}>
                                        {it.cantidad}
                                    </div>

                                    <div className="flex-grow-1" style={{ lineHeight: '1.1' }}>
                                        <div className={`font-weight-bold text-uppercase ${esNuevo && order.status !== 'Lista' ? 'text-danger' : 'text-dark'}`} style={{ fontSize: '1rem' }}>
                                            {it.nombre}
                                        </div>
                                        <RenderVariantesDetalle texto={getVariantesTexto(it)} fontSize="0.8rem" />
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>

            {/* --- BOTON DE ACCIÓN --- */}
            <div className="p-2">
                <button
                    className={`btn btn-block btn-lg shadow-sm font-weight-bold text-white border-0 py-3`}
                    style={{
                        borderRadius: '15px',
                        background: order.status === 'Recibida' ? 'linear-gradient(135deg, #ffd861 0%, #fcb045 100%)' :
                            (order.status === 'Lista' ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' :
                                'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                        color: order.status === 'Recibida' ? '#000' : '#fff'
                    }}
                    onClick={() => onStatusChange(order, 'NEXT')}
                >
                    {order.status === 'Recibida' ? 'EMPEZAR COCINA' : (order.status === 'Lista' ? 'ENTREGAR' : 'LISTO')}
                </button>
            </div>
        </div>
    );
};

// ==============================================================================
// 🖥️ COMPONENTE PRINCIPAL KDS
// ==============================================================================
const KDS = () => {
    const { orders, fetchOrders } = usePedidosKDS();
    const [inputBuffer, setInputBuffer] = useState("");
    const [alarmaPago, setAlarmaPago] = useState(null);
    const [showZoom, setShowZoom] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showRepartidor, setShowRepartidor] = useState(false);
    const [repartidores, setRepartidores] = useState([]);
    const [recienFinalizados, setRecienFinalizados] = useState({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [pedidoAAnular, setPedidoAAnular] = useState(null);
    // 🆕 Filtro y comprobante
    const [filtroTipo, setFiltroTipo] = useState('TODOS');
    const [comprobanteUrl, setComprobanteUrl] = useState(null);

    const FILTROS = [
        { key: 'TODOS', label: '📍 Todos', icon: 'fa-globe' },
        { key: 'DELIVERY', label: '🚚 Delivery', icon: 'fa-motorcycle' },
        { key: 'RETIRO', label: '🛍️ Retiro', icon: 'fa-shopping-bag' },
        { key: 'MESA', label: '🍽️ Mesa', icon: 'fa-utensils' },
        { key: 'APP', label: '📱 App', icon: 'fa-mobile-alt' },
    ];

    useEffect(() => {
        if (REPARTIDOR_KDS_HABILITADO) getRepartidoresActivos().then(setRepartidores);
    }, []);

    // 🚩 1. DETECTOR DE PAGOS: Inicia cronómetro de 2 minutos cuando el pedido llega a "Lista" y está pagado.
    useEffect(() => {
        const nuevosFinalizados = { ...recienFinalizados };
        let huboCambios = false;

        orders.forEach(o => {
            const estaPagado = o.pagado === 1 || o.pagado === true;
            const esLocal = !o.type || !o.type.toUpperCase().includes('DELIVERY');

            if (o.status === 'Lista' && esLocal && estaPagado) {
                if (!nuevosFinalizados[o.id]) {
                    nuevosFinalizados[o.id] = Date.now();
                    huboCambios = true;
                }
            }
        });

        if (huboCambios) setRecienFinalizados(nuevosFinalizados);
    }, [orders]);

    // 🚩 2. EL MOTOR DE AUTO-CIERRE
    useEffect(() => {
        var timer = setInterval(function () {
            var ahora = Date.now();
            var copia = { ...recienFinalizados };
            var cambio = false;

            for (var id in copia) {
                const segundosTranscurridos = Math.floor((ahora - copia[id]) / 1000);
                if (segundosTranscurridos >= 120) {
                    ejecutarCambioEnServidor(id, 'Entregada');
                    delete copia[id];
                    cambio = true;
                }
            }
            if (cambio) setRecienFinalizados(copia);
        }, 1000);
        return () => clearInterval(timer);
    }, [recienFinalizados]);

    // --- 🛠️ FUNCIÓN MAESTRA DE CAMBIO DE ESTADO ---
    const handleStatusChange = async (pedido, direccion = 'NEXT') => {
        if (!pedido || isProcessing) return;

        var id = pedido.id;
        var nextStatus = null;
        const estaPagado = pedido.pagado === 1 || pedido.pagado === true;

        if (direccion === 'BACK') {
            if (pedido.status === 'Preparando') nextStatus = 'Recibida';
            else if (pedido.status === 'Lista') nextStatus = 'Preparando';
            else if (pedido.status === 'EnReparto') nextStatus = 'Lista';
            else return;
        } else {
            if (pedido.status === 'Recibida') nextStatus = 'Preparando';
            else if (pedido.status === 'Preparando') nextStatus = 'Lista';
            else if (pedido.status === 'Lista') {
                if (REPARTIDOR_KDS_HABILITADO && pedido.type && pedido.type.toUpperCase().includes('DELIVERY')) {
                    setSelectedOrder(pedido);
                    setShowRepartidor(true);
                    return;
                }
                else nextStatus = pedido.type && pedido.type.toUpperCase().includes('DELIVERY') ? 'EnReparto' : 'Entregada';
            }
            else if (pedido.status === 'EnReparto') nextStatus = 'Entregada';
        }

        if (!nextStatus) return;

        if (nextStatus === 'Entregada' && !estaPagado) {
            setAlarmaPago(pedido.display_id);
            return;
        }

        await ejecutarCambioEnServidor(id, nextStatus);
    };

    const ejecutarCambioEnServidor = async (pedidoId, statusName) => {
        setIsProcessing(true);
        try {
            var token = localStorage.getItem("token");
            const res = await fetch(config.api_url.replace(/\/$/, "") + "/api/v1/pedidos/" + pedidoId + "/cambiar_estado", {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'token': token },
                body: JSON.stringify({ nuevoEstadoId: STATUS_TO_ID[statusName] })
            });
            if (res.ok) await fetchOrders();
        } catch (err) {
            toast.error("Error al conectar con el servidor");
        } finally {
            setIsProcessing(false);
        }
    };

    const asignarRepartidor = async (repartidorId) => {
        if (!selectedOrder || isProcessing) return;
        setIsProcessing(true);
        try {
            var token = localStorage.getItem("token");
            const res = await fetch(config.api_url.replace(/\/$/, "") + "/api/pedidos/asignar_repartidor", {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'token': token },
                body: JSON.stringify({ pedido_id: selectedOrder.id, repartidor_id: repartidorId })
            });
            if (res.ok) {
                setShowRepartidor(false);
                await ejecutarCambioEnServidor(selectedOrder.id, 'EnReparto');
            } else {
                toast.error("Error al asignar repartidor");
            }
        } catch (e) {
            toast.error("Error de red");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAnularPedido = async (pedido) => {
        setIsProcessing(true);
        try {
            await anularPedidoApi(pedido.id);
            await fetchOrders();
        } catch (e) {
            toast.error("Error al anular");
        } finally {
            setIsProcessing(false);
        }
    };

    const ejecutarComandoKDS = async (numero, accion) => {
        var p = orders.find(o => String(o.display_id) === String(numero));
        if (!p) return toast.error("No existe #" + numero);

        if (accion === 'IMPRIMIR') {
            const d = {
                id: p.id, display_id: p.display_id, total: p.total,
                nombre_cliente: p.customer ? (p.customer.name || p.customer.nombre) : 'VENTA',
                direccion: p.customer ? (p.customer.address || '') : '',
                observacion: p.observacion || '', type: p.type,
                cartItems: p.items.map(i => ({ cantidad: i.cantidad, nombre: i.nombre, variantes: getVariantesTexto(i) }))
            };
            imprimirComandaCocina(d);
        }
    };

    // --- ⌨️ MANEJO DE TECLADO ---
    useEffect(() => {
        const manejarTeclado = (e) => {
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || isProcessing) return;

            if (alarmaPago) {
                if (['Enter', '.', 'Escape', 'Delete', 'Backspace'].includes(e.key)) { setAlarmaPago(null); setInputBuffer(""); }
                return;
            }

            if (pedidoAAnular) {
                if (e.key === 'Enter') {
                    handleAnularPedido(pedidoAAnular); setPedidoAAnular(null); setInputBuffer("");
                } else if (['.', '/', '-', '+', '*', 'Escape', 'Delete', 'Backspace'].includes(e.key)) {
                    setPedidoAAnular(null); setInputBuffer("");
                }
                return;
            }

            if (showZoom) {
                const container = document.getElementById('zoom-scroll-container');
                if (container) {
                    if (e.key === '/') { e.preventDefault(); container.scrollBy({ top: -300, behavior: 'smooth' }); return; }
                    if (e.key === '-') { e.preventDefault(); container.scrollBy({ top: 300, behavior: 'smooth' }); return; }
                }
                if (['Enter', '*', 'Escape', '.', 'Delete', 'Backspace'].includes(e.key)) { setShowZoom(false); setInputBuffer(""); }
                return;
            }

            if (/[0-9]/.test(e.key)) {
                setInputBuffer(p => (p + e.key).slice(-6));
                return;
            }

            if (['.', ',', 'Delete', 'Backspace', 'Escape'].includes(e.key)) {
                e.preventDefault(); setInputBuffer(""); setShowRepartidor(false); return;
            }

            if (inputBuffer) {
                if (inputBuffer.startsWith("00") && inputBuffer.length > 2) {
                    if (['Enter', '-', '/', '+', '*'].includes(e.key)) {
                        e.preventDefault();
                        const idRealParaBorrar = inputBuffer.replace(/^00+/, '');
                        const pToKill = orders.find(o => String(o.display_id) === String(idRealParaBorrar));
                        if (pToKill) setPedidoAAnular(pToKill);
                        else { toast.warning(`No existe #${idRealParaBorrar}`); setInputBuffer(""); }
                        return;
                    }
                }

                const pedidoNormal = orders.find(o => String(o.display_id) === String(inputBuffer));
                if (!pedidoNormal) {
                    if (['Enter', '/', '-', '+', '*'].includes(e.key)) { toast.warning(`No existe #${inputBuffer}`); setInputBuffer(""); }
                    return;
                }

                if (e.key === '/') { e.preventDefault(); handleStatusChange(pedidoNormal, 'BACK'); setInputBuffer(""); return; }
                if (e.key === '-') { e.preventDefault(); handleStatusChange(pedidoNormal, 'NEXT'); setInputBuffer(""); return; }
                if (e.key === 'Enter') {
                    e.preventDefault();
                    if (showRepartidor) {
                        const idx = parseInt(inputBuffer) - 1;
                        if (repartidores[idx]) asignarRepartidor(repartidores[idx].id);
                        else toast.error("Repartidor inválido");
                    } else {
                        handleStatusChange(pedidoNormal, 'NEXT');
                    }
                    setInputBuffer(""); return;
                }
                if (e.key === '+') { e.preventDefault(); ejecutarComandoKDS(inputBuffer, 'IMPRIMIR'); setInputBuffer(""); return; }
                if (e.key === '*') { e.preventDefault(); setSelectedOrder(pedidoNormal); setShowZoom(true); setInputBuffer(""); return; }
            }
        };

        document.addEventListener('keydown', manejarTeclado);
        return () => document.removeEventListener('keydown', manejarTeclado);
    }, [inputBuffer, orders, showZoom, showRepartidor, repartidores, isProcessing, selectedOrder, alarmaPago, pedidoAAnular]);

    // 🆕 FILTRAR ORDENES POR TIPO + COLUMNA
    const filtrarPorTipo = (order) => {
        if (filtroTipo === 'TODOS') return true;
        const type = (order.type || "").toUpperCase();
        const origen = (order.origen || order.plataforma_origen || "").toUpperCase();
        const metodoPago = (order.metodo_pago || "").toUpperCase();

        switch (filtroTipo) {
            case 'DELIVERY': return type.includes('DELIVERY');
            case 'RETIRO': return type.includes('RETIRO');
            case 'MESA': return origen.includes('MESA') || type.includes('LOCAL') || (!type.includes('DELIVERY') && !type.includes('RETIRO') && !metodoPago.includes('PLATAFORMA') && !origen.includes('WEB'));
            case 'APP': return type.includes('APP') || metodoPago === 'PLATAFORMA';
            default: return true;
        }
    };

    var activeOrders = orders
        .filter(o => o.status !== 'Entregada')
        .filter(filtrarPorTipo)
        .sort((a, b) => new Date(a.time) - new Date(b.time));

    return (
        <div className="min-vh-100 bg-dark p-2 text-white text-center">
            {isProcessing && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="spinner-border text-warning" style={{ width: '5rem', height: '5rem' }} role="status"></div>
                    <h2 className="ml-4 font-weight-bold">PROCESANDO...</h2>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={2000} theme="dark" />

            <style>{`
                @keyframes warning-flash { 0% { border: 6px solid #ffc107; } 50% { border: 6px solid #dc3545; background-color: #ffdce0; } 100% { border: 6px solid #ffc107; } }
                .anim-warning-flash { animation: warning-flash 0.4s infinite; }
                
                @keyframes blink-slow { 0% { background-color: #fff; } 50% { background-color: #d4edda; border-color: #28a745; } 100% { background-color: #fff; } }
                @keyframes blink-fast { 0% { background-color: #fff; } 50% { background-color: #28a745; color: white !important; } 100% { background-color: #fff; } }
                .anim-blink-slow { animation: blink-slow 2s infinite; }
                .anim-blink-fast { animation: blink-fast 0.5s infinite; }

                .custom-scrollbar::-webkit-scrollbar { width: 16px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #555; border-radius: 10px; border: 3px solid #fff; }

                /* 🆕 ESTILOS DE FILTROS */
                .kds-filtros {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    margin-bottom: 12px;
                }
                .kds-filtro-btn {
                    background: transparent;
                    border: 2px solid #444;
                    color: #aaa;
                    padding: 8px 20px;
                    border-radius: 30px;
                    font-weight: 700;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s;
                    text-transform: uppercase;
                }
                .kds-filtro-btn:hover {
                    border-color: #888;
                    color: #fff;
                }
                .kds-filtro-btn.active {
                    background: #ffc107;
                    border-color: #ffc107;
                    color: #000;
                }
                .kds-filtro-contador {
                    background: rgba(255,255,255,0.15);
                    border-radius: 10px;
                    padding: 1px 8px;
                    margin-left: 6px;
                    font-size: 0.75rem;
                }
                .kds-filtro-btn.active .kds-filtro-contador {
                    background: rgba(0,0,0,0.15);
                }
            `}</style>

            {/* 🆕 BARRA DE FILTROS */}
            <div className="kds-filtros">
                {FILTROS.map(f => {
                    const count = orders.filter(o => {
                        if (o.status === 'Entregada') return false;
                        if (f.key === 'TODOS') return true;
                        const type = (o.type || "").toUpperCase();
                        const origen = (o.origen || o.plataforma_origen || "").toUpperCase();
                        const metodoPago = (o.metodo_pago || "").toUpperCase();
                        switch (f.key) {
                            case 'DELIVERY': return type.includes('DELIVERY');
                            case 'RETIRO': return type.includes('RETIRO');
                            case 'MESA': return origen.includes('MESA') || (!type.includes('DELIVERY') && !type.includes('RETIRO') && !metodoPago.includes('PLATAFORMA') && !origen.includes('WEB'));
                            case 'APP': return type.includes('APP') || metodoPago === 'PLATAFORMA';
                            default: return true;
                        }
                    }).length;

                    return (
                        <button key={f.key}
                            className={`kds-filtro-btn ${filtroTipo === f.key ? 'active' : ''}`}
                            onClick={() => setFiltroTipo(f.key)}>
                            {f.label}
                            <span className="kds-filtro-contador">{count}</span>
                        </button>
                    );
                })}
            </div>

            <div className="row no-gutters">
                {['Recibida', 'Preparando', 'Lista', 'EnReparto'].map(function (col) {
                    var pedidosCol = activeOrders.filter(o => o.status === col);
                    return (
                        <div key={col} className="col-md-3 px-1">
                            <div className={"p-2 rounded-top font-weight-bold text-uppercase " + (col === 'Preparando' ? 'bg-warning text-dark' : (col === 'Lista' ? 'bg-success text-white' : 'bg-secondary text-white'))}>
                                {col} ({pedidosCol.length})
                            </div>
                            <div className="p-2 bg-light rounded-bottom shadow-inner custom-scrollbar" style={{ height: '85vh', overflowY: 'auto' }}>
                                {pedidosCol.map(function (order) {
                                    var tiempoEnLista = recienFinalizados[order.id] ? Math.floor((Date.now() - recienFinalizados[order.id]) / 1000) : null;

                                    return <OrderCard
                                        key={order.id} order={order}
                                        onStatusChange={(ord, dir) => handleStatusChange(ord, dir)}
                                        onReimprimir={(p) => ejecutarComandoKDS(p.display_id, 'IMPRIMIR')}
                                        onZoom={(p) => { setSelectedOrder(p); setShowZoom(true); }}
                                        onVerComprobante={(url) => setComprobanteUrl(url)}
                                        tiempoEnLista={tiempoEnLista}
                                    />;
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {inputBuffer && (
                <div className="position-fixed" style={{ bottom: '20px', left: '20px', zIndex: 110000 }}>
                    <div className="bg-warning text-dark p-3 rounded-xl shadow-lg border-2 border-dark">
                        <h1 className="display-2 font-weight-bold m-0">#{inputBuffer}</h1>
                    </div>
                </div>
            )}

            {showZoom && <ZoomOrderModal order={selectedOrder} onClose={() => setShowZoom(false)}
                onVerComprobante={(url) => setComprobanteUrl(url)} />}

            {/* 🆕 MODAL DE COMPROBANTE */}
            {comprobanteUrl && (
                <ComprobanteModal url={comprobanteUrl} onClose={() => setComprobanteUrl(null)} />
            )}

            {REPARTIDOR_KDS_HABILITADO && showRepartidor && (
                <div className="modal-custom" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 100005, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(8px)' }}>
                    <div className="modal-radar p-5 shadow-2xl w-100 text-white" style={{ maxWidth: '650px', borderRadius: '40px' }}>
                        <div className="text-center mb-5">
                            <h1 className="display-4 font-weight-black mt-3 text-uppercase">Despacho Activo</h1>
                            <p className="text-warning h4 font-weight-bold">SELECCIONA REPARTIDOR PARA #{selectedOrder ? selectedOrder.display_id : ''}</p>
                        </div>
                        <div className="row">
                            {repartidores.map((r, i) => (
                                <div key={r.id} className="col-12 mb-3">
                                    <button className="btn btn-dark btn-block p-4 d-flex align-items-center shadow-lg" style={{ borderRadius: '20px', textAlign: 'left', backgroundColor: '#111' }} onClick={() => asignarRepartidor(r.id)}>
                                        <div className="bg-warning text-dark rounded-circle d-flex align-items-center justify-content-center mr-4" style={{ width: '70px', height: '70px', minWidth: '70px', fontSize: '2.5rem', fontWeight: '900' }}>{i + 1}</div>
                                        <div className="flex-grow-1"><div className="h2 font-weight-black m-0 text-uppercase">{r.nombre}</div></div>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {alarmaPago && (
                <div className="modal-custom" onClick={() => setAlarmaPago(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(220,53,69,0.98)', zIndex: 100002, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <h1 style={{ fontSize: '10rem' }} className="font-weight-bold">#{alarmaPago}</h1>
                    <h1 className="font-weight-bold text-uppercase">PAGO PENDIENTE</h1>
                    <button className="btn btn-light btn-lg mt-5 rounded-pill px-5 shadow font-weight-bold" onClick={() => setAlarmaPago(null)}>CERRAR (ENTER)</button>
                </div>
            )}

            {pedidoAAnular && (
                <div className="modal-custom" style={{ position: 'fixed', inset: 0, background: 'rgba(220,53,69,0.98)', zIndex: 100005, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white', animation: 'warning-flash 0.5s infinite' }}>
                    <i className="fas fa-trash-alt mb-3" style={{ fontSize: '6rem' }}></i>
                    <h1 style={{ fontSize: '8rem' }} className="font-weight-bold">#{pedidoAAnular.display_id}</h1>
                    <h1 className="font-weight-bold text-uppercase mb-5">¿ESTÁS SEGURO DE ANULAR ESTE PEDIDO?</h1>
                    <div className="d-flex w-75 justify-content-around mt-4">
                        <div className="text-center p-3 bg-dark rounded-xl shadow-lg border border-secondary">
                            <h2 className="text-danger font-weight-bold">SÍ, ANULAR</h2>
                            <h4 className="text-white mt-2 mb-0">PRESIONA [ ENTER ]</h4>
                        </div>
                        <div className="text-center p-3 bg-light rounded-xl shadow-lg border border-secondary">
                            <h2 className="text-success font-weight-bold">CANCELAR</h2>
                            <h4 className="text-dark mt-2 mb-0">PRESIONA [ PUNTO . ]</h4>
                        </div>
                    </div>
                </div>
            )}

            <div className="kds-footer">
                <div className="d-flex">
                    <div className="kds-shortcut" title="Escribe el número y presiona Enter">
                        <span className="kds-key">N° + ENTER</span> Avanzar Estado
                    </div>
                    <div className="kds-shortcut" title="Escribe el número y presiona Guión">
                        <span className="kds-key">N° + /</span> Retroceder
                    </div>
                    <div className="kds-shortcut">
                        <span className="kds-key">N° + *</span> Zoom / Ver Detalle
                    </div>
                    <div className="kds-shortcut">
                        <span className="kds-key">N° + +</span> Reimprimir
                    </div>
                </div>
                <div className="d-flex align-items-center">
                    <div className="kds-shortcut mr-0">
                        <span className="kds-key kds-key-special">00 + N° + ENTER</span> <span className="text-danger font-weight-bold">ANULAR</span>
                    </div>
                    <div className="ml-3 small text-muted">
                        <i className="fas fa-wifi text-success mr-1"></i> EN LÍNEA
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KDS;